import { Plugin } from '@nuxt/types';
import { AxiosInstance } from 'axios';
import { createApiInstance } from '~/utils/api-factory';

/**
 * API V2 Service Container Interface
 * Provides access to different microservices under the API V2 infrastructure
 *
 * @example this.$apiV2.default.get('/endpoint')
 * @example this.$apiV2.subscription.get('/user-permissions/456')
 */
export interface ApiV2Services {
  /**
   * Default Service - General API V2 endpoints
   * @example this.$apiV2.default.get('/endpoint')
   */
  default: AxiosInstance;

  /**
   * Subscription Service - Handles user subscriptions
   * @example this.$apiV2.subscription.get('/user-permissions/456')
   */
  subscription: AxiosInstance;
}

declare module 'vue/types/vue' {
  interface Vue {
    $apiV2: ApiV2Services;
  }
}

declare module '@nuxt/types' {
  interface NuxtAppOptions {
    $apiV2: ApiV2Services;
  }
  interface Context {
    $apiV2: ApiV2Services;
  }
}

const apiV2Plugin: Plugin = (context, inject) => {
  // Service instance cache for lazy loading
  const serviceCache: Partial<Record<keyof ApiV2Services, AxiosInstance>> = {};

  const config = {
    apiV2Url: context.$config.API_V2_URL || '<%= options.apiV2Url %>',
    apiV2SubscriptionUrl:
      context.$config.API_V2_SUBSCRIPTION_URL ||
      '<%= options.apiV2SubscriptionUrl %>'
  };
  /**
   * Lazy-loaded service container
   * Services are initialized on first access to save memory
   */
  const apiV2: ApiV2Services = {
    get default(): AxiosInstance {
      if (!serviceCache.default) {
        serviceCache.default = createApiInstance(config.apiV2Url, context);
      }
      return serviceCache.default;
    },

    get subscription(): AxiosInstance {
      if (!serviceCache.subscription) {
        serviceCache.subscription = createApiInstance(
          config.apiV2SubscriptionUrl,
          context
        );
      }
      return serviceCache.subscription;
    }
  };

  // Inject $apiV2 into context
  inject('apiV2', apiV2);
};

export default apiV2Plugin;
